import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { apiClient, ApiError } from '../../../lib/apiClient';
import { HELP_TICKET_CATEGORIES, type HelpTicketCategory } from './helpCategories';
import { stepFromSlug } from '../wizardSteps';
import {
  readDraftCampaignName,
  readDraftContactName,
} from './ticketContactInfo';
import {
  buildTicketCampaignSnapshot,
  encodeTicketDetailsWithSnapshot,
  readWizardDraftFormData,
} from './ticketCampaignSnapshot';
import { useStationHelpBranding } from './helpStationBranding';
import StationHelpModalHeader from './StationHelpModalHeader';
import PrimaryButton from '../shared/PrimaryButton';
import { accentVars, tintFromAccent } from '../steps/_shared';

interface AskDeveloperModalProps {
  open: boolean;
  onClose: () => void;
}

const FIELD_CLASS =
  'mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--accent,#94a3b8)] focus:border-[color:var(--accent,#94a3b8)]';

const AskDeveloperModal: React.FC<AskDeveloperModalProps> = ({ open, onClose }) => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { config, accent, primary } = useStationHelpBranding(slug);

  const [category, setCategory] = useState<HelpTicketCategory | null>(null);
  const [issueTitle, setIssueTitle] = useState('');
  const [details, setDetails] = useState('');
  const [contactName, setContactName] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);

  const stepMatch = location.pathname.match(/\/start\/([^/]+)/);
  const wizardStep = stepFromSlug(stepMatch?.[1]) ?? undefined;

  useEffect(() => {
    if (!open) return;
    setCategory(null);
    setIssueTitle('');
    setDetails('');
    setError(null);
    setTicketId(null);
    setContactName(
      (slug ? readDraftContactName(slug) : '') || user?.contactName || '',
    );
    setCampaignName(slug ? readDraftCampaignName(slug) : '');
    setEmail(user?.email ?? '');
  }, [open, user?.contactName, user?.email, slug]);

  if (!open) return null;

  const submittedEmail = (isAuthenticated ? user?.email ?? '' : email).trim();
  const modalStyle = {
    fontFamily: config?.fontFamily,
    ...(accent ? accentVars(accent, primary) : {}),
  };

  const categoryOptionStyle = (selected: boolean): React.CSSProperties | undefined => {
    if (!selected || !accent) return undefined;
    return {
      borderColor: accent,
      backgroundColor: tintFromAccent(accent, 0.06),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !slug) return;
    const trimmedDetails = details.trim();
    const trimmedEmail = submittedEmail;
    const trimmedName = contactName.trim();
    const trimmedIssueTitle = issueTitle.trim();
    if (category === 'other' && !trimmedIssueTitle) {
      setError('Please enter a title for your issue.');
      return;
    }
    if (!trimmedDetails) {
      setError('Please describe your issue.');
      return;
    }
    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }
    if (!trimmedName) {
      setError('Your name is required.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const draftForm = readWizardDraftFormData(slug);
      const snapshot = buildTicketCampaignSnapshot(
        draftForm,
        { includePayment: wizardStep === 5 },
        config ?? undefined,
      );

      const res = await apiClient.post<{ ok: true; id: string }>(
        '/support/tickets',
        {
          stationSlug: slug,
          email: trimmedEmail,
          contactName: trimmedName,
          category,
          details: encodeTicketDetailsWithSnapshot(
            snapshot,
            campaignName,
            trimmedDetails,
            category === 'other' ? trimmedIssueTitle : undefined,
          ),
          wizardStep: wizardStep ?? undefined,
          pageUrl: `${window.location.origin}${location.pathname}${location.search}`,
        },
        { anonymous: !isAuthenticated },
      );
      setTicketId(res.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/45"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ask-developer-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col rounded-xl bg-white shadow-xl border border-slate-200"
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <StationHelpModalHeader config={config} onClose={onClose} />

        <div className="px-5 py-4 border-b border-slate-100 shrink-0">
          <h2 id="ask-developer-title" className="text-lg font-semibold text-slate-900">
            Ask our support team
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            What is going wrong? Pick the closest option and tell us more below.
          </p>
        </div>

        <div className="overflow-y-auto p-5">
          {ticketId ? (
            <div className="space-y-4 text-sm text-slate-700">
              <p className="font-medium text-slate-900">Thanks — your ticket was submitted.</p>
              <p>
                We will follow up at <strong>{submittedEmail}</strong>.
                {isAuthenticated && ' Track status in Help Center.'}
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                {slug && isAuthenticated && (
                  <PrimaryButton
                    type="button"
                    accentColor={accent}
                    primaryColor={primary}
                    onClick={() => {
                      onClose();
                      navigate(`/${slug}/help`);
                    }}
                  >
                    Open Help Center
                  </PrimaryButton>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div
                className="space-y-3 rounded-lg border px-3 py-3"
                style={
                  accent
                    ? { borderColor: tintFromAccent(accent, 0.25), backgroundColor: tintFromAccent(accent, 0.04) }
                    : { borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }
                }
              >
                <p
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: accent ?? '#64748b' }}
                >
                  Your information
                </p>

                <label className="block text-sm">
                  <span className="font-medium text-slate-800">
                    Your name
                    <span className="text-red-500 ml-0.5" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <input
                    type="text"
                    required
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className={FIELD_CLASS}
                    autoComplete="name"
                  />
                </label>

                <label className="block text-sm">
                  <span className="font-medium text-slate-800">Campaign name</span>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    className={FIELD_CLASS}
                  />
                </label>

                <label className="block text-sm">
                  <span className="font-medium text-slate-800">
                    Email
                    <span className="text-red-500 ml-0.5" aria-hidden="true">
                      *
                    </span>
                  </span>
                  {isAuthenticated ? (
                    <div className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                      {user?.email}
                    </div>
                  ) : (
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={FIELD_CLASS}
                      autoComplete="email"
                    />
                  )}
                </label>
              </div>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-slate-800 mb-2">Issue type</legend>
                {HELP_TICKET_CATEGORIES.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                      category === opt.value
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    style={categoryOptionStyle(category === opt.value)}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={opt.value}
                      checked={category === opt.value}
                      onChange={() => {
                        setCategory(opt.value);
                        if (opt.value !== 'other') setIssueTitle('');
                      }}
                      className="mt-1"
                    />
                    <span className="text-sm text-slate-800">{opt.label}</span>
                  </label>
                ))}
              </fieldset>

              {category && (
                <>
                  {category === 'other' && (
                    <label className="block text-sm">
                      <span className="font-medium text-slate-800">Issue title</span>
                      <input
                        type="text"
                        required
                        value={issueTitle}
                        onChange={(e) => setIssueTitle(e.target.value)}
                        className={FIELD_CLASS}
                        placeholder="Short summary of your issue"
                      />
                    </label>
                  )}

                  <label className="block text-sm">
                    <span className="font-medium text-slate-800">Tell us more</span>
                    <textarea
                      required
                      rows={5}
                      value={details}
                      onChange={(e) => setDetails(e.target.value)}
                      className={`${FIELD_CLASS} resize-y min-h-[120px]`}
                      placeholder="What were you trying to do? What happened instead?"
                    />
                  </label>
                </>
              )}

              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <PrimaryButton
                type="submit"
                disabled={submitting || !category}
                accentColor={accent}
                primaryColor={primary}
                fullWidth
                size="md"
                className="!rounded-lg !py-2.5"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit ticket
              </PrimaryButton>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AskDeveloperModal;
